import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { enc, HmacSHA256, MD5 } from 'crypto-js';
import { environment } from 'environments/environment';
import { of } from 'rxjs';
import { catchError, map, take, timeout } from 'rxjs/operators';

import { TransactionRequest } from '../models/transaction-request';
import { GlobalFunctionsService } from './global-functions.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private clientIp: string;

  constructor(
    private http: HttpClient,
    private globalFunctionsService: GlobalFunctionsService,
    @Inject('BACKEND_URL') private backendUrl: string,
    @Inject('BUCKAROO_URL') private buckarooUrl: string,
    @Inject('BUCKAROO_SECRET') private buckarooSecret: string,
    @Inject('BUCKAROO_WEBSITEKEY') private buckarooWebsiteKey: string
  ) {}

  // Get client IPv4 address.
  getClientIp(): void {
    this.http
      .get('https://ip4.seeip.org/json')
      .pipe(
        timeout(2000),
        map((res: { ip: string }) => res.ip),
        catchError(() => of(null))
      )
      .subscribe((ip: string) => {
        this.clientIp = ip;
      });
  }

  // Request a transaction through Buckaroo (https://dev.buckaroo.nl/Apis).
  requestTransaction(amount: number, invoiceReference: string, issuer: string): void {
    const course = this.globalFunctionsService.getSelectedCourse().Name;
    const transactionRequest: TransactionRequest = {
      AmountDebit: amount,
      Currency: environment.buckaroo.currency,
      Invoice: invoiceReference,
      Services: {
        ServiceList: [
          {
            Name: 'ideal',
            Action: 'Pay',
            Parameters: [
              {
                Name: 'issuer',
                Value: issuer
              }
            ]
          }
        ]
      },
      // ContinueOnIncomplete: true,
      // ServicesSelectableByClient: 'ideal',
      Description: `Inschrijving voor de cursus ${course}.`,
      ReturnURL: `${this.backendUrl}/payment/success`,
      ReturnURLCancel: `${this.backendUrl}/payment/cancel`,
      ReturnURLError: `${this.backendUrl}/payment/error`,
      ReturnURLReject: `${this.backendUrl}/payment/reject`,
      PushURL: `${this.backendUrl}/payment/push`
    };

    // Add client IP if available.
    if (this.clientIp) {
      transactionRequest.ClientIP = {
        Type: 0,
        Address: this.clientIp
      };
    }

    // Create authentication header (https://dev.buckaroo.nl/Apis/Description/json).
    const authHeader = this.getAuthHeader(
      `${this.buckarooUrl}/json/Transaction`,
      this.buckarooWebsiteKey,
      this.buckarooSecret,
      JSON.stringify(transactionRequest),
      'POST'
    );

    this.http
      .post(`${this.backendUrl}/payment/request`, { request: transactionRequest, authHeader }, { responseType: 'json' })
      .pipe(take(1))
      .subscribe(response => (window.location.href = response['url']), err => console.log(err));
  }

  private getAuthHeader(
    requestUri: string,
    websiteKey: string,
    secretKey: string,
    content: any,
    httpMethod: 'GET' | 'POST'
  ): string {
    const nonce = this.getNonce();
    const timeStamp = this.getTimeStamp();
    content = content ? content : '';
    const url = encodeURIComponent(requestUri).toLowerCase();
    return `hmac ${websiteKey}:${this.getHash(
      websiteKey,
      secretKey,
      httpMethod,
      nonce,
      timeStamp,
      url,
      content
    )}:${nonce}:${timeStamp}`;
  }

  private getNonce(): string {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < 16; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  private getTimeStamp(): number {
    return Math.floor(new Date().getTime() / 1000);
  }

  private getHash(
    websiteKey: string,
    secretKey: string,
    httpMethod: string,
    nonce: string,
    timeStamp: number,
    requestUri: string,
    content: string
  ): string {
    const encodedContent = this.getEncodedContent(content);
    const rawData = websiteKey + httpMethod + requestUri + timeStamp + nonce + encodedContent;
    const hash = HmacSHA256(rawData, secretKey);
    const hashInBase64 = enc.Base64.stringify(hash);
    return hashInBase64;
  }

  private getEncodedContent(content: string): string {
    if (content) {
      const md5 = MD5(content);
      const base64 = enc.Base64.stringify(md5);
      return base64;
    }
    return content;
  }
}
